import * as React from 'react';
import { ChevronDown, ChevronsUpDown, ExternalLink, MoreHorizontal } from 'lucide-react';

import { cn } from '@/lib/cn';
import { Badge, type BadgePlan } from '../badge';
import { FOCUS_RING } from '../kit';
import { Collapse } from '../collapse';
import { OrgAvatar } from '../org-avatar';
import { Pressable } from '../pressable';
import { useActiveIndicator } from '../use-active-indicator';

/**
 * Sidebar of the panel: a full-height black column (ink with sheen, never
 * glass; in dark it stays near-black with a hairline). Compose it from the
 * `Sidebar*` pieces below; `MobileDrawer` takes the same children, so the
 * phone menu is the desktop sidebar.
 *
 * The black is a token re-scope (`[data-slot="sidebar"]` in kit-next.css), so
 * every kit piece inside (Badge, OrgAvatar, focus rings) follows it without
 * dark variants.
 */
export function Sidebar({ className, children, ...props }: React.ComponentProps<'aside'>) {
  return (
    <aside
      data-slot="sidebar"
      className={cn(
        'sticky top-3 z-10 m-3 mr-0 flex h-[calc(100dvh-1.5rem)] flex-col gap-1 overflow-hidden rounded-card border border-transparent px-3 pb-3 pt-3.5 text-ink',
        className,
      )}
      {...props}
    >
      {children}
    </aside>
  );
}

type AnchorLikeProps = { as?: React.ElementType } & Record<string, unknown>;

const WORDMARK = (
  <>
    urbania<span className="text-brand-ink">360</span>
  </>
);

/** The urbania360 mark and wordmark (no link). Shared by the sidebar and the phone top bar. */
export function BrandWordmark({ className }: { className?: string }) {
  return (
    <span data-slot="brand-wordmark" className={cn('inline-flex items-center gap-2.5 text-base font-bold tracking-tight text-ink', className)}>
      <span
        data-slot="brand-mark"
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-control bg-ink text-sm font-extrabold text-surface"
        aria-hidden="true"
      >
        U
      </span>
      <span>{WORDMARK}</span>
    </span>
  );
}

/** Brand at the top of the sidebar; links home. Pass `as={Link} to="/admin"` for the router. */
export function SidebarBrand({ as: Component = 'a', className, ...props }: AnchorLikeProps & { className?: string }) {
  return (
    <Component
      data-slot="sidebar-brand"
      aria-label="urbania360, ir al inicio"
      className={cn('mb-1 flex shrink-0 items-center rounded-control px-1 pb-3 pt-0.5', FOCUS_RING, className)}
      {...props}
    >
      <BrandWordmark />
    </Component>
  );
}

export interface SidebarOrgProps extends Omit<React.ComponentProps<'button'>, 'children'> {
  name: string;
  logoUrl?: string | null;
  /** Plan tag ("Basic"). */
  planLabel?: string;
  /** Which plan, for the tag's colours (defaults to Basic). */
  planId?: BadgePlan;
}

/**
 * Organization block. It is the TRIGGER of the org switcher menu: wrap it in
 * `<DropdownMenuTrigger asChild>` and open the content with `side="right"`.
 */
export function SidebarOrg({ name, logoUrl, planLabel, planId = 'basic', className, ...props }: SidebarOrgProps) {
  return (
    <Pressable
      data-slot="sidebar-org"
      aria-label={`Organización ${name}`}
      className={cn('mb-2.5 flex w-full shrink-0 items-center gap-2.5 rounded-card border border-line p-2 text-left', className)}
      {...props}
    >
      <OrgAvatar name={name} logoUrl={logoUrl} size="lg" className="size-[34px] text-xs" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold leading-tight text-ink">{name}</span>
        {planLabel ? (
          <Badge plan={planId} className="mt-0.5">
            {planLabel}
          </Badge>
        ) : null}
      </span>
      <ChevronsUpDown className="size-4 shrink-0 text-ink-3" aria-hidden="true" />
    </Pressable>
  );
}

/** Scrollable middle of the sidebar: nav items, sections, separators. */
export function SidebarNav({ className, children, ...props }: React.ComponentProps<'nav'>) {
  const ref = React.useRef<HTMLElement>(null);
  // One pill that slides to the current page (drawn by the redesigned kit; hidden otherwise).
  useActiveIndicator(ref, '[data-slot="sidebar-item"][aria-current="page"], [data-slot="sidebar-item"][data-active]');
  return (
    <nav
      ref={ref}
      data-slot="sidebar-nav"
      aria-label="Navegación principal"
      className={cn('flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain', className)}
      {...props}
    >
      <span aria-hidden="true" data-slot="nav-indicator" className="pointer-events-none hidden" />
      {children}
    </nav>
  );
}

export type SidebarNavItemProps = AnchorLikeProps & {
  icon?: React.ReactNode;
  label: React.ReactNode;
  /** Count chip at the end (tours of a collection). 0 hides it unless `showZero`. */
  count?: number;
  showZero?: boolean;
  /** The current page: white pill with ink text. Also sets `aria-current`. */
  active?: boolean;
  /** Opens elsewhere (new tab): adds the external-link icon. */
  external?: boolean;
  /** Collection color dot, or `"dashed"` for the "Sin colección" bucket. Replaces `icon`. */
  dot?: string;
  /** Brand turquoise text, for the creating action ("Nueva colección"). */
  tone?: 'default' | 'brand';
  className?: string;
};

/**
 * One row of the sidebar. A link by default; render another element with `as`
 * (`as={NavLink} to=… end`), or `as="button"` for an action. `NavLink` marks the
 * current page with `aria-current`, which the style honours too.
 */
export function SidebarNavItem({
  as: Component = 'a',
  icon,
  label,
  count,
  showZero = false,
  active,
  external,
  dot,
  tone = 'default',
  className,
  ...props
}: SidebarNavItemProps) {
  const showCount = typeof count === 'number' && (count > 0 || showZero);
  return (
    <Component
      data-slot="sidebar-item"
      data-active={active ? '' : undefined}
      data-tone={tone === 'brand' ? 'brand' : undefined}
      aria-current={active ? 'page' : undefined}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={cn(
        'group flex h-9 w-full shrink-0 items-center gap-2.5 rounded-control px-2.5 text-left text-sm font-medium text-ink-2',
        FOCUS_RING,
        className,
      )}
      {...props}
    >
      {dot ? (
        <span
          data-slot="sidebar-dot"
          data-dashed={dot === 'dashed' ? '' : undefined}
          className="mx-1 size-2 shrink-0 rounded-pill"
          style={dot === 'dashed' ? undefined : { backgroundColor: dot }}
          aria-hidden="true"
        />
      ) : icon ? (
        <span data-slot="sidebar-icon" className="inline-flex shrink-0 [&_svg]:size-4">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate">{label}</span>
      {external ? <ExternalLink className="ml-auto size-3.5 shrink-0 text-ink-3" aria-hidden="true" /> : null}
      {showCount ? (
        <span data-slot="sidebar-count" className="ml-auto min-w-[22px] shrink-0 rounded-control px-1.5 py-1 text-center text-xs font-semibold tabular-nums leading-none">
          {count}
        </span>
      ) : null}
    </Component>
  );
}

export interface SidebarSectionProps {
  /** Section label ("Colecciones"), shown small and uppercase. */
  label: string;
  /** Adds a chevron and lets the section collapse. */
  collapsible?: boolean;
  /** Controlled state; use with `onCollapsedChange`. */
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  children?: React.ReactNode;
  className?: string;
}

/** A labelled group of rows, optionally collapsible (the label is then the toggle). */
export function SidebarSection({
  label,
  collapsible = false,
  collapsed: collapsedProp,
  defaultCollapsed = false,
  onCollapsedChange,
  children,
  className,
}: SidebarSectionProps) {
  const [inner, setInner] = React.useState(defaultCollapsed);
  const collapsed = collapsedProp ?? inner;
  const bodyId = React.useId();
  const toggle = () => {
    const next = !collapsed;
    if (collapsedProp === undefined) setInner(next);
    onCollapsedChange?.(next);
  };
  return (
    <div data-slot="sidebar-section" className={cn('flex flex-col gap-0.5', className)}>
      {collapsible ? (
        <Pressable
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          data-slot="sidebar-section-label"
          className="mt-3.5 flex w-full items-center gap-1 rounded-control px-2.5 pb-1.5 pt-1 text-2xs font-bold uppercase tracking-widest text-ink-3 transition-colors hover:text-ink-2"
        >
          {label}
          <ChevronDown className={cn('size-3.5 transition-transform duration-(--dur-2) ease-(--ease-out)', collapsed && '-rotate-90')} aria-hidden="true" />
        </Pressable>
      ) : (
        <p data-slot="sidebar-section-label" className="mt-3.5 px-2.5 pb-1.5 pt-1 text-2xs font-bold uppercase tracking-widest text-ink-3">
          {label}
        </p>
      )}
      <Collapse id={bodyId} open={!collapsed} className="flex flex-col gap-0.5">
        {children}
      </Collapse>
    </div>
  );
}

export function SidebarSeparator({ className }: { className?: string }) {
  return <div data-slot="sidebar-separator" role="separator" className={cn('mx-1.5 my-2.5 h-px shrink-0 bg-line', className)} />;
}

export interface SidebarUserProps extends Omit<React.ComponentProps<'button'>, 'children'> {
  name: string;
  avatarUrl?: string | null;
  /** Second line; "Mi cuenta" by default. */
  subtitle?: string;
}

/**
 * Signed-in user at the bottom of the sidebar (avatar circle, name, "Mi cuenta",
 * ⋯). It is the TRIGGER of the account menu: wrap it in `<DropdownMenuTrigger asChild>`
 * and open the content upward (`side="top"`).
 */
export function SidebarUser({ name, avatarUrl, subtitle = 'Mi cuenta', className, ...props }: SidebarUserProps) {
  return (
    <Pressable
      data-slot="sidebar-user"
      aria-label={`Cuenta de ${name}`}
      className={cn('mt-auto flex w-full shrink-0 items-center gap-2.5 rounded-card border border-line p-2 text-left', className)}
      {...props}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="size-8 shrink-0 rounded-pill object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-pill bg-surface-sunken text-xs font-bold text-ink-2" aria-hidden="true">
          {(name.trim()[0] ?? '?').toUpperCase()}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold leading-tight text-ink">{name}</span>
        <span className="block truncate text-xs text-ink-3">{subtitle}</span>
      </span>
      <MoreHorizontal className="size-4 shrink-0 text-ink-3" aria-hidden="true" />
    </Pressable>
  );
}
